import {
  QueryObserverResult,
  UseMutationResult,
  UseQueryResult,
} from '@tanstack/react-query'

export type ServiceConfigName = 'account' | 'featureFlags'

export type GetOptions = {
  key?: string
  params?: Record<string, string | number>
  enabled?: boolean
}

export type PostOptions = {
  key?: string
}

export type ServerStateResponse<TData> = {
  status: number
  headers: {[key: string]: string | string[] | number | boolean | null | any}
  data: TData
}

export class ServerStateError<T> extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly data: T,
  ) {
    super(message)
  }
}

export type ServerStateGetResult<TData, TError> = QueryObserverResult<
  ServerStateResponse<TData>,
  ServerStateError<TError>
>

export type ServerStateGetQueryResult<TData, TError> = Omit<
  UseQueryResult<ServerStateResponse<TData>, ServerStateError<TError>>,
  'refetch'
> & {
  get: () => Promise<ServerStateGetResult<TData, TError>>
  reset: () => Promise<void>
}

export type BodyVariables<T> = {
  body?: T
  params?: Record<string, string | number>
}

type ServerStateMutationResult<TData, TError, TVariables> = Omit<
  UseMutationResult<
    ServerStateResponse<TData>,
    ServerStateError<TError>,
    TVariables
  >,
  'mutateAsync' | 'mutate'
>

export type ServerStatePostResult<
  TData,
  TError = ServerStateError<void>,
  TVariables = unknown,
> = ServerStateMutationResult<TData, TError, TVariables> & {
  post: (data?: TVariables) => void
  postAsync: (data?: TVariables) => Promise<ServerStateResponse<TData>>
}

export type ServerStatePutResult<
  TData,
  TError = ServerStateError<void>,
  TVariables = unknown,
> = ServerStateMutationResult<TData, TError, TVariables> & {
  put: (data?: TVariables) => void
  putAsync: (data?: TVariables) => Promise<ServerStateResponse<TData>>
}

export type ServerStatePatchResult<
  TData,
  TError = ServerStateError<void>,
  TVariables = unknown,
> = ServerStateMutationResult<TData, TError, TVariables> & {
  patch: (data?: TVariables) => void
  patchAsync: (data?: TVariables) => Promise<ServerStateResponse<TData>>
}

export type ServerStateDeleteResult<
  TData,
  TError = ServerStateError<void>,
  TVariables = unknown,
> = ServerStateMutationResult<TData, TError, TVariables> & {
  delete: (data?: TVariables) => void
  deleteAsync: (data?: TVariables) => Promise<ServerStateResponse<TData>>
}

export type ServerState = {
  useGet: <TData, TError = ServerStateError<void>>(
    url: string,
    options?: GetOptions,
  ) => ServerStateGetQueryResult<TData, TError>
  usePost: <TData, TError = ServerStateError<void>, TVariables = unknown>(
    url: string,
  ) => ServerStatePostResult<TData, TError, BodyVariables<TVariables>>
  usePut: <TData, TError = ServerStateError<void>, TVariables = unknown>(
    url: string,
  ) => ServerStatePutResult<TData, TError, BodyVariables<TVariables>>
  usePatch: <TData, TError = ServerStateError<void>, TVariables = unknown>(
    url: string,
  ) => ServerStatePatchResult<TData, TError, BodyVariables<TVariables>>
  useDelete: <
    TData,
    TVariables = Record<string, string | number>,
    TError = ServerStateError<void>,
  >(
    url: string,
  ) => ServerStateDeleteResult<TData, TError, TVariables>
}
